import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';

export type Language = 'fr' | 'en' | 'es' | 'de';

export const languageFormatLocales: Record<Language, string> = {
  fr: 'fr-FR',
  en: 'en-GB',
  es: 'es-ES',
  de: 'de-DE',
};

interface LanguageContextType {
  language: Language;
  formatLocale: string;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translationsCache: Record<Language, Record<string, any>> = {
  fr: {},
  en: {},
  es: {},
  de: {},
};

const loadTranslations = async (lang: Language): Promise<Record<string, any>> => {
  if (Object.keys(translationsCache[lang]).length > 0) {
    return translationsCache[lang];
  }

  try {
    const translations = await import(`../translations/${lang}.json`);
    translationsCache[lang] = translations.default;
    return translations.default;
  } catch (error) {
    console.error(`Failed to load translations for ${lang}:`, error);
    return {};
  }
};

const getNestedValue = (obj: any, path: string): string | undefined => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

const replaceParams = (str: string, params?: Record<string, string | number>): string => {
  if (!params) return str;
  return Object.entries(params).reduce((result, [key, value]) => 
    result.replace(new RegExp(`{{${key}}}`, 'g'), String(value)), str
  );
};

interface LanguageProviderProps {
  children: ReactNode;
  defaultLanguage?: Language;
}

export const LanguageProvider = ({ 
  children, 
  defaultLanguage = 'fr' 
}: LanguageProviderProps) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem('app_language') as Language;
    return stored || defaultLanguage;
  });
  
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const preloadAllLanguages = async () => {
      const languages: Language[] = ['fr', 'en', 'es', 'de'];
      await Promise.all(languages.map(lang => loadTranslations(lang)));
      setIsReady(true);
    };
    preloadAllLanguages();
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    if (lang === language) return;
    setLanguageState(lang);
    localStorage.setItem('app_language', lang);
    document.documentElement.lang = lang;
  }, [language]);

  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    if (!isReady) return key;
    const value = getNestedValue(translationsCache[language], key);
    if (value === undefined) {
      console.warn(`Translation missing for key: ${key}`);
      return key;
    }
    return replaceParams(value, params);
  }, [language, isReady]);

  const formatLocale = languageFormatLocales[language];
  const contextValue = useMemo(
    () => ({ language, formatLocale, setLanguage, t }),
    [language, formatLocale, setLanguage, t]
  );

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
